// Fetch-poort voor GET-verzoeken naar /api/ (PERFORMANCE.md, Startup contract).
//
// Gemeten op prod 8 okt 2026: een vers tabblad vuurt 58 (Vandaag) tot 88
// (kandidaatrecord) API-verzoeken tegelijk af op een kleine Neon-compute. Onder die
// golf duurt /api/bootstrap 600 tot 950 ms in plaats van 127 ms, en de rijen van het
// CRM-grid 1000 ms in plaats van 220 ms. De JS is na 250 ms klaar; de wachttijd is
// puur databasecontentie.
//
// De poort laat bootstrap en de primaire gegevens van het zichtbare scherm direct
// door en zet de rest in een FIFO-rij: niets uit de rij start voordat bootstrap
// klaar is (of een veiligheidstimer afgaat), en daarna lopen er hooguit `limit`
// tegelijk. Schrijvende methodes (de grid-zoekactie is een POST) gaan er altijd
// langs. De kern gebruikt geen browserglobals; installFetchGate koppelt hem aan
// window.fetch.

export const GATE_LIMIT = 4;
export const GATE_HOLD_MS = 2500;
// Een route die geen bootstrap doet (/director, /match, /login) hoeft niet de volle
// veiligheidstimer te wachten: start er binnen deze tijd na installatie geen
// bootstrap, dan gaat de poort open.
export const GATE_BOOTSTRAP_GRACE_MS = 400;
// Een hangend verzoek houdt zijn plek niet voor altijd vast: na deze tijd telt het
// niet meer mee (het verzoek zelf loopt gewoon door).
export const GATE_SLOT_TIMEOUT_MS = 8000;

const BASE = 'http://fetch-gate.invalid';
const BOOTSTRAP_PATH = '/api/bootstrap';
const PRIORITY_PATHS = new Set([
  BOOTSTRAP_PATH,
  '/api/version',
  '/api/auth/me',
  '/api/deals',
  '/api/inbox-unread',
]);
const PRIORITY_CRM_RESOURCES = new Set(['profile', 'search', 'functies']);

function isRequestLike(input) {
  return !!input && typeof input === 'object' && typeof input.url === 'string' && typeof input.method === 'string';
}

function inputUrl(input) {
  if (typeof input === 'string') return input;
  if (isRequestLike(input)) return input.url;
  if (input && typeof input.href === 'string') return input.href; // URL-object
  return null;
}

// Pad + query van een same-origin URL, anders null.
export function parseSameOrigin(input, origin) {
  const raw = inputUrl(input);
  if (raw == null) return null;
  let u;
  try { u = new URL(raw, origin || BASE); } catch { return null; }
  const isRelative = !/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.startsWith('//');
  if (!isRelative && (!origin || u.origin !== new URL(origin).origin)) return null;
  return u;
}

function methodOf(input, init) {
  const m = (init && init.method) || (isRequestLike(input) ? input.method : 'GET');
  return String(m || 'GET').toUpperCase();
}

function signalOf(input, init) {
  if (init && init.signal) return init.signal;
  if (isRequestLike(input) && input.signal) return input.signal;
  return null;
}

// Same-origin GET naar /api/ zonder body.
export function isApiGet(input, init, origin) {
  if (methodOf(input, init) !== 'GET') return false;
  if (init && init.body != null) return false;
  if (isRequestLike(input) && input.body != null) return false;
  const u = parseSameOrigin(input, origin);
  return !!u && u.pathname.startsWith('/api/');
}

export function isBootstrapUrl(input, origin) {
  const u = parseSameOrigin(input, origin);
  return !!u && u.pathname === BOOTSTRAP_PATH;
}

// Bootstrap en de primaire gegevens van het zichtbare scherm: nooit in de rij,
// tellen niet mee voor de limiet.
export function isPriorityApiGet(input, init, origin) {
  if (init && init.priority === 'high') return true;
  const signal = signalOf(input, init);
  if (signal && signal.aborted) return true;
  const u = parseSameOrigin(input, origin);
  if (!u) return false;
  if (PRIORITY_PATHS.has(u.pathname)) return true;
  // Het record zelf, de globale zoekbalk en de functietitel-typeahead: wat de
  // gebruiker zojuist aanklikte of typte wacht niet achter de achtergrondrij.
  if (u.pathname === '/api/crm' && PRIORITY_CRM_RESOURCES.has(u.searchParams.get('resource'))) return true;
  return false;
}

function abortReason(signal) {
  if (signal && signal.reason !== undefined) return signal.reason;
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

export function createFetchGate({
  limit = GATE_LIMIT,
  holdUntil = GATE_HOLD_MS,
  slotTimeoutMs = GATE_SLOT_TIMEOUT_MS,
  isGated = (input, init) => isApiGet(input, init),
  isPriority = (input, init) => isPriorityApiGet(input, init),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) {
  const queue = [];
  let active = 0;
  let maxActive = 0;
  let released = false;
  let unlimited = false;

  const holdTimer = holdUntil > 0 && Number.isFinite(holdUntil) ? setTimer(() => release(), holdUntil) : null;

  function pump() {
    if (!released) return;
    while (queue.length && (unlimited || active < limit)) start(queue.shift());
  }

  function start(entry) {
    if (entry.signal && entry.onAbort) entry.signal.removeEventListener('abort', entry.onAbort);
    active += 1;
    if (active > maxActive) maxActive = active;
    let freed = false;
    let slotTimer = null;
    const free = () => {
      if (freed) return;
      freed = true;
      if (slotTimer != null) clearTimer(slotTimer);
      active -= 1;
      pump();
    };
    if (slotTimeoutMs > 0 && Number.isFinite(slotTimeoutMs)) slotTimer = setTimer(free, slotTimeoutMs);
    let p;
    try { p = Promise.resolve(entry.doFetch()); } catch (e) { p = Promise.reject(e); }
    p.then((res) => { free(); entry.resolve(res); }, (err) => { free(); entry.reject(err); });
  }

  function release() {
    if (released) return;
    released = true;
    if (holdTimer != null) clearTimer(holdTimer);
    pump();
  }

  // Voor het verwijderen van de poort: alles wat nog in de rij staat start meteen.
  function drain() {
    unlimited = true;
    release();
    pump();
  }

  function run(input, init, doFetch) {
    if (!isGated(input, init) || isPriority(input, init)) return doFetch();
    return new Promise((resolve, reject) => {
      const entry = { doFetch, resolve, reject, signal: signalOf(input, init), onAbort: null };
      if (entry.signal && typeof entry.signal.addEventListener === 'function') {
        entry.onAbort = () => {
          const i = queue.indexOf(entry);
          if (i !== -1) { queue.splice(i, 1); reject(abortReason(entry.signal)); }
        };
        entry.signal.addEventListener('abort', entry.onAbort, { once: true });
      }
      queue.push(entry);
      pump();
    });
  }

  return {
    run,
    release,
    drain,
    get released() { return released; },
    get active() { return active; },
    get queued() { return queue.length; },
    get maxActive() { return maxActive; },
  };
}

// Wikkelt win.fetch eenmalig. Geeft een functie terug die de oorspronkelijke fetch
// terugzet en de rij leegmaakt.
export function installFetchGate(win = globalThis.window, opts = {}) {
  if (!win || typeof win.fetch !== 'function') return () => {};
  if (win.fetch.__pbFetchGate) return () => {};
  const {
    bootstrapGraceMs = GATE_BOOTSTRAP_GRACE_MS,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (id) => clearTimeout(id),
    ...gateOpts
  } = opts;
  const origin = (win.location && win.location.origin) || undefined;
  const original = win.fetch;
  const gate = createFetchGate({
    setTimer,
    clearTimer,
    ...gateOpts,
    isGated: (input, init) => isApiGet(input, init, origin),
    isPriority: (input, init) => isPriorityApiGet(input, init, origin),
  });
  let bootstrapSeen = false;
  const graceTimer = bootstrapGraceMs > 0
    ? setTimer(() => { if (!bootstrapSeen) gate.release(); }, bootstrapGraceMs)
    : null;

  function gatedFetch(...args) {
    const [input, init] = args;
    const call = () => original.apply(this, args);
    if (isBootstrapUrl(input, origin)) {
      bootstrapSeen = true;
      const p = call();
      Promise.resolve(p).then(() => gate.release(), () => gate.release());
      return p;
    }
    return gate.run(input, init, call);
  }
  gatedFetch.__pbFetchGate = true;
  gatedFetch.gate = gate;
  win.fetch = gatedFetch;

  return function uninstall() {
    if (graceTimer != null) clearTimer(graceTimer);
    if (win.fetch === gatedFetch) win.fetch = original;
    gate.drain();
  };
}
